import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

interface DocumentSummaryResponse {
  summary: string;
}

const METHOD = 'GET';
const ENDPOINT = 'document/:id/summary';
export const QUERY_KEY = (id: string) => getQueryKey(METHOD, ENDPOINT, { id });

/**
 * Hook to fetch document summary from the API
 * @param id The ID of the document to get summary for
 * @param enabled Whether the query should be enabled (defaults to true)
 * @returns Query result with document summary data
 */
export const useGetDocumentSummary = (id: string, enabled = true) => {
  const { session } = useAuth();

  return useQuery<string>({
    queryKey: QUERY_KEY(id),
    queryFn: async () => {
      const response = await handleApiCall<DocumentSummaryResponse>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          id: id.toString(),
        })
      );

      return response.summary;
    },
    enabled: enabled && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};
